"use client";
import { cn } from "@/lib/utils";
import React from "react";
import { BentoGrid, BentoGridItem } from "../ui/bento-grid";
import {
  IconBrandTwitter,
  IconBrandLinkedin,
  IconBrandInstagram,
  IconBrandYoutube,
  IconBrandReddit,
} from "@tabler/icons-react";
import {
  Tags,
  Search,
  Sparkles,
  Inbox,
  Brain,
  Hash,
  Globe,
  Layers,
} from "lucide-react";
import Image from "next/image"; // Import Image component
import { CSSReveal } from "@/components/motion";
import { motion } from "motion/react";

export default function Features() {
  return (
    <section id="features" className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <CSSReveal>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-1/10 text-brand-1 text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            <span>Core Capabilities</span>
          </div>
        </CSSReveal>
        <CSSReveal delay={0.1}>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            Designed for your{" "}
            <span className="bg-gradient-to-r from-brand-1 to-brand-3 bg-clip-text text-transparent">
              digital memory
            </span>
          </h2>
        </CSSReveal>
        <CSSReveal delay={0.2}>
          <p className="text-neutral-400 max-w-2xl mx-auto text-lg">
            Powerful tools to capture the best ideas you find online, organize
            them automatically, and pull them back up right when you need them
          </p>
        </CSSReveal>
      </div>

      <BentoGrid className="max-w-6xl mx-auto md:auto-rows-[22rem]">
        {items.map((item, i) => (
          <BentoGridItem
            key={i}
            title={item.title}
            description={item.description}
            header={item.header}
            className={cn("[&>p:text-lg]", item.className)}
            icon={item.icon}
          />
        ))}
      </BentoGrid>
    </section>
  );
}

// --- Visual Components ---

const CollectionsVisual = () => {
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const collections = [
    {
      name: "Viral Hooks",
      description: "50+ viral hooks",
      count: 52,
      color: "#f472b6", // Pink
      icon: "🔥",
    },
    {
      name: "Dream Desk",
      description: "Workspace inspo",
      count: 18,
      color: "#60a5fa", // Blue
      icon: "💻",
    },
    {
      name: "Japan 2026",
      description: "Hidden gems",
      count: 85,
      color: "#34d399", // Emerald
      icon: "🇯🇵",
    },
    {
      name: "SaaS Ideas",
      description: "Micro-SaaS list",
      count: 12,
      color: "#a78bfa", // Purple
      icon: "💡",
    },
    {
      name: "AI Prompts",
      description: "Midjourney v6",
      count: 140,
      color: "#fbbf24", // Amber
      icon: "🎨",
    },
    {
      name: "Fitness",
      description: "Gym routines",
      count: 24,
      color: "#ef4444", // Red
      icon: "💪",
    },
  ];

  return (
    <div className="flex flex-1 w-full h-full min-h-[6rem] rounded-xl bg-neutral-900 border border-neutral-800 p-5 relative overflow-hidden flex-col justify-center">
      <div className="grid grid-cols-2 gap-3 w-full relative z-10">
        {collections.map((collection, i) => (
          <motion.div
            key={i}
            whileHover={{ scale: 1.02, x: 2 }}
            className="group relative overflow-hidden rounded-lg border bg-neutral-900/80 p-3 transition-all duration-300 hover:shadow-lg hover:border-neutral-700 cursor-default flex items-center gap-3"
            style={{
              borderColor: hexToRgba(collection.color, 0.15),
              background: `linear-gradient(90deg, ${hexToRgba(
                collection.color,
                0.05
              )}, transparent 90%)`,
            }}
          >
            {/* Left accent bar */}
            <div
              className="absolute left-0 top-0 bottom-0 w-[2px] opacity-60"
              style={{ backgroundColor: collection.color }}
            />

            {/* Icon */}
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${
                  collection.color
                } 0%, ${hexToRgba(collection.color, 0.7)} 100%)`,
                boxShadow: `0 4px 12px -2px ${hexToRgba(
                  collection.color,
                  0.3
                )}`,
              }}
            >
              <span className="text-sm drop-shadow-sm">{collection.icon}</span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-neutral-100 truncate pr-2">
                  {collection.name}
                </h4>
                <span className="text-[9px] font-mono text-neutral-500 bg-neutral-800/80 px-1 py-0.5 rounded border border-neutral-700/50 shrink-0">
                  {collection.count}
                </span>
              </div>
              <p className="text-[10px] text-neutral-400 truncate font-medium mt-0.5">
                {collection.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Decorative elements */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-brand-1/10 rounded-full blur-[60px] pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-500/10 rounded-full blur-[60px] pointer-events-none" />
    </div>
  );
};

const UnifiedInboxVisual = () => {
  const items = [
    {
      icon: <IconBrandTwitter className="w-3.5 h-3.5 text-sky-500" />,
      user: "@vercel",
      content: "Next.js 15 is now stable 🚢",
      time: "2m",
      bg: "bg-sky-500/10",
    },
    {
      icon: <IconBrandLinkedin className="w-3.5 h-3.5 text-blue-600" />,
      user: "Guillermo Rauch",
      content: "The future of the web is...",
      time: "15m",
      bg: "bg-blue-600/10",
    },
    {
      icon: <IconBrandReddit className="w-3.5 h-3.5 text-orange-500" />,
      user: "r/webdev",
      content: "How to optimize React perf?",
      time: "1h",
      bg: "bg-orange-500/10",
    },
  ];

  return (
    <div className="flex flex-1 w-full h-full min-h-[6rem] rounded-xl bg-neutral-900 border border-neutral-800 p-4 relative overflow-hidden flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
            Live Feed
          </span>
        </div>
        <span className="text-[10px] text-neutral-600 font-mono">
          New Saves
        </span>
      </div>

      {/* Feed Items */}
      <div className="flex flex-col gap-2 relative z-10">
        {items.map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.15 }}
            whileHover={{ scale: 1.02, x: 2 }}
            className="flex items-start gap-3 p-2.5 rounded-lg bg-neutral-800/40 border border-neutral-700/50 hover:bg-neutral-800/60 hover:border-neutral-600 transition-colors cursor-default group"
          >
            <div className={cn("p-1.5 rounded-md shrink-0 mt-0.5", item.bg)}>
              {item.icon}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[10px] font-bold text-neutral-300">
                  {item.user}
                </span>
                <span className="text-[9px] text-neutral-600">{item.time}</span>
              </div>
              <p className="text-[10px] text-neutral-400 leading-tight line-clamp-1 group-hover:text-neutral-300 transition-colors">
                {item.content}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Bottom Fade */}
      <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-neutral-900 to-transparent pointer-events-none" />
    </div>
  );
};

const SmartTagsVisual = () => {
  const tags = [
    {
      name: "Productivity",
      style: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    },
    {
      name: "SaaS",
      style: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    },
    {
      name: "Marketing",
      style: "bg-pink-500/10 text-pink-400 border-pink-500/20",
    },
    {
      name: "Design",
      style: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    },
    { name: "AI", style: "bg-green-500/10 text-green-400 border-green-500/20" },
    {
      name: "Startup",
      style: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    },
    {
      name: "Fitness",
      style: "bg-yellow-500/10 text-black-400 border-yellow-500/20",
    },
    {
      name: "Fashion",
      style: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    },
    {
      name: "Hiking",
      style: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
    },
  ];

  return (
    <div className="flex flex-1 w-full h-full min-h-[6rem] rounded-xl bg-neutral-900 border border-neutral-800 p-6 relative overflow-hidden flex-col items-center justify-center">
      <div className="flex flex-wrap gap-3 justify-center content-center max-w-[280px]">
        {tags.map((tag, i) => (
          <motion.div
            key={tag.name}
            initial={{ y: 0, opacity: 0 }}
            animate={{
              y: [0, -8, 0],
              opacity: 1,
            }}
            transition={{
              y: {
                duration: 2 + Math.random() * 1.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.1,
              },
              opacity: { duration: 0.5, delay: i * 0.1 },
            }}
            whileHover={{ scale: 1.1, zIndex: 10 }}
            className={cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-full border shadow-sm cursor-default backdrop-blur-sm",
              tag.style
            )}
          >
            <Hash className="w-3 h-3 opacity-70" />
            <span className="text-sm font-medium">{tag.name}</span>
          </motion.div>
        ))}
      </div>

      {/* Decorative Blur */}
      <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-brand-1/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
    </div>
  );
};

const SearchVisual = () => {
  const [typedText, setTypedText] = React.useState("");
  const [showResults, setShowResults] = React.useState(false);
  const [exampleIndex, setExampleIndex] = React.useState(0);

  const examples = [
    {
      query: "marketing strat",
      user: "@marketing_guru",
      platform: "twitter",
      text: (
        <>
          The best{" "}
          <span className="bg-brand-1/20 text-brand-1 px-0.5 rounded">
            marketing strat
          </span>
          egy is simply caring about your customers more than...
        </>
      ),
    },
    {
      query: "react performance",
      user: "Frontend Mastery",
      platform: "youtube",
      text: (
        <>
          5 tips to boost{" "}
          <span className="bg-brand-1/20 text-brand-1 px-0.5 rounded">
            react performance
          </span>{" "}
          instantly using useMemo and...
        </>
      ),
    },
    {
      query: "startup funding",
      user: "Paul Graham",
      platform: "linkedin",
      text: (
        <>
          When looking for{" "}
          <span className="bg-brand-1/20 text-brand-1 px-0.5 rounded">
            startup funding
          </span>
          , remember that investors invest in people, not just...
        </>
      ),
    },
  ];

  const currentExample = examples[exampleIndex];

  React.useEffect(() => {
    let isMounted = true;

    const animate = async () => {
      while (isMounted) {
        // Reset
        setTypedText("");
        setShowResults(false);
        await new Promise((r) => setTimeout(r, 500));

        // Type current query
        const textToType = examples[exampleIndex].query; // Use ref or fresh state if needed, but index is stable in this closure scope?
        // Actually, exampleIndex in this closure will be stale if we don't use a ref or dependency.
        // Better approach: use a local variable for the loop or standard recursion with dependency.
        // Let's use a simpler recursion approach to ensure fresh state.
      }
    };
    // The previous loop approach with stale state is risky.
    // Let's use a chain of timeouts controlled by a separate effect that depends on exampleIndex.
    return () => {
      isMounted = false;
    };
  }, []); // logic needs rewrite for rotating index

  // Better animation logic for rotating content
  React.useEffect(() => {
    let isMounted = true;

    const runSequence = async () => {
      // 1. Reset UI (already hidden from previous cycle end, or initial)
      setTypedText("");
      setShowResults(false);
      await new Promise((r) => setTimeout(r, 200));
      if (!isMounted) return;

      // 2. Type Text
      const query = examples[exampleIndex].query;
      for (let i = 0; i <= query.length; i++) {
        if (!isMounted) return;
        setTypedText(query.slice(0, i));
        await new Promise((r) => setTimeout(r, 30 + Math.random() * 20));
      }

      // 3. Show Results
      if (!isMounted) return;
      await new Promise((r) => setTimeout(r, 100));
      setShowResults(true);

      // 4. Hold
      if (!isMounted) return;
      await new Promise((r) => setTimeout(r, 2000));

      // 5. Next Example
      if (!isMounted) return;
      setShowResults(false);
      setTypedText("");
      await new Promise((r) => setTimeout(r, 100)); // fade out time

      setExampleIndex((prev) => (prev + 1) % examples.length);
    };

    runSequence();

    return () => {
      isMounted = false;
    };
  }, [exampleIndex]);

  const getIcon = (platform: string) => {
    switch (platform) {
      case "twitter":
        return <IconBrandTwitter className="w-3 h-3 text-blue-400" />;
      case "youtube":
        return <IconBrandYoutube className="w-3 h-3 text-red-500" />;
      case "linkedin":
        return <IconBrandLinkedin className="w-3 h-3 text-blue-600" />;
      default:
        return <Search className="w-3 h-3 text-neutral-400" />;
    }
  };

  return (
    <div className="flex flex-1 w-full h-full min-h-[6rem] rounded-xl bg-neutral-900 border border-neutral-800 p-6 flex-col relative overflow-hidden">
      {/* Search Input Mockup */}
      <div className="w-full relative mb-6 group">
        <div className="absolute inset-0 bg-brand-1/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg" />
        <div className="relative bg-neutral-950 border border-neutral-700 rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
          <Search className="w-5 h-5 text-brand-1" />
          <div className="flex-1 font-medium text-neutral-200 flex items-center h-6">
            {typedText}
            <motion.div
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.8, repeat: Infinity }}
              className="w-0.5 h-5 bg-brand-1 ml-0.5"
            />
          </div>
          <div className="text-xs text-neutral-600 border border-neutral-800 rounded px-1.5 py-0.5">
            ⌘K
          </div>
        </div>
      </div>

      {/* Result Cards */}
      <div className="space-y-3 relative z-0 h-[120px]">
        <motion.div
          key={currentExample.query} // Re-mount for fresh animation
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: showResults ? 1 : 0,
            y: showResults ? 0 : 10,
          }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-neutral-800/40 border border-neutral-700/50 rounded-lg p-3 hover:bg-neutral-800/60 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full bg-neutral-800 flex items-center justify-center">
              {getIcon(currentExample.platform)}
            </div>
            <span className="text-xs text-neutral-400">
              {currentExample.user}
            </span>
          </div>
          <p className="text-sm text-neutral-300 line-clamp-2">
            {currentExample.text}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: showResults ? 0.6 : 0,
            y: showResults ? 0 : 10,
          }}
          transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
          className="bg-neutral-800/20 border border-neutral-800 rounded-lg p-3"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full bg-neutral-800 flex items-center justify-center">
              <Hash className="w-3 h-3 text-neutral-500" />
            </div>
            <span className="text-xs text-neutral-500">Tags</span>
          </div>
          <div className="h-2 w-full bg-neutral-800 rounded mb-1.5" />
          <div className="h-2 w-2/3 bg-neutral-800 rounded" />
        </motion.div>
      </div>

      {/* Fade out bottom */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-neutral-900 to-transparent pointer-events-none" />
    </div>
  );
};

const items = [
  {
    title: "Playlists for the Internet",
    description:
      "Mix saves from Twitter, LinkedIn, YouTube, and Instagram into unified, shareable collections. Curate references for projects, clients, or visual inspiration.",
    header: <CollectionsVisual />,
    className: "md:col-span-2",
    icon: <Layers className="h-4 w-4 text-neutral-500" />,
  },
  {
    title: "Unified Social Inbox",
    description:
      "One home for every post, thread, and video you save. Tavlo normalizes messy social content into a clean library built for retrieval and reuse.",
    header: <UnifiedInboxVisual />,
    className: "md:col-span-1",
    icon: <Inbox className="h-4 w-4 text-neutral-500" />,
  },
  {
    title: "AI Insights & Summary",
    description:
      "Get the gist in seconds. Tavlo automatically summarizes long threads and videos, generating smart tags and categories so your library stays organized.",
    header: <SmartTagsVisual />,
    className: "md:col-span-1",
    icon: <Tags className="h-4 w-4 text-neutral-500" />,
  },
  {
    title: "Searchable Second Brain",
    description:
      "Finally, a way to search your Twitter bookmarks and LinkedIn saves. Type a phrase and find the exact insight in seconds, across all platforms.",
    header: <SearchVisual />,
    className: "md:col-span-2",
    icon: <Search className="h-4 w-4 text-neutral-500" />,
  },
];
